import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card } from '@/components/ui';
import { apiFetch } from '@/data/api';
import { FormError } from '@/components/crm/Modal';

type Outcome = 'create' | 'link' | 'unchanged' | 'skipped_staff' | 'skipped_no_email' | 'error';
interface Row { company: string; email: string | null; name: string | null; outcome: Outcome; message?: string }
interface Page { dryRun: boolean; results: Row[]; nextOffset: number | null; totalCompanies: number; seen: string[] }

const LABEL: Record<Outcome, string> = {
  create: 'new contacts', link: 'linked to another client', unchanged: 'already there',
  skipped_staff: 'Triton staff skipped', skipped_no_email: 'without an email skipped', error: 'clients Clerk could not read',
};

/* Reads each linked client's tritonIQ logins from Clerk and files them as
   contacts. Batches of 20 clients per request; the loop lives here. */
export function TritoniqContacts() {
  const qc = useQueryClient();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [mode, setMode] = useState<'preview' | 'imported' | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const run = async (dryRun: boolean) => {
    setError(null); setRows(null);
    const all: Row[] = [];
    let offset: number | null = 0;
    let seen: string[] = [];
    try {
      while (offset !== null) {
        const page: Page = await apiFetch<Page>('/api/import/tritoniq-contacts', { method: 'POST', body: JSON.stringify({ offset, dryRun, seen }) });
        all.push(...page.results);
        seen = page.seen;
        setProgress(`${dryRun ? 'Checked' : 'Imported'} ${Math.min(page.nextOffset ?? page.totalCompanies, page.totalCompanies)} of ${page.totalCompanies} clients…`);
        offset = page.nextOffset;
      }
      setRows(all);
      setMode(dryRun ? 'preview' : 'imported');
      if (!dryRun) for (const k of ['contacts', 'companies', 'company', 'audit']) void qc.invalidateQueries({ queryKey: [k] });
    } catch (e) { setError(e); }
    finally { setProgress(null); }
  };

  const count = (o: Outcome) => rows?.filter((r) => r.outcome === o).length ?? 0;
  const toImport = count('create') + count('link');
  return (
    <Card padding={false} className="crm-section crm-settings-card">
      <div className="crm-section__head"><h3>Contacts from tritonIQ logins</h3></div>
      <p className="crm-cell-muted" style={{ marginBottom: 12 }}>
        Adds everyone who logs in to tritonIQ for a client as a contact on that company. Triton staff are skipped. Contacts are records
        only — this gives nobody access to clientIQ and changes nothing in tritonIQ. Safe to run again; people who don't use tritonIQ
        still need adding by hand or by sheet import.
      </p>
      <div className="crm-record__actions">
        <Button variant="outline" onClick={() => void run(true)} disabled={!!progress}>Preview</Button>
        <Button onClick={() => void run(false)} disabled={!!progress || mode !== 'preview' || !toImport}>
          {mode === 'preview' && toImport ? `Import ${toImport}` : 'Import'}
        </Button>
        {progress && <span className="crm-cell-sub">{progress}</span>}
      </div>
      <FormError error={error} />
      {rows && (
        <div className="crm-callout" style={{ marginTop: 14 }}>
          <strong>{mode === 'preview' ? 'Preview — nothing saved yet' : 'Imported'}</strong>
          <p>{(Object.keys(LABEL) as Outcome[]).filter((o) => count(o)).map((o) => `${count(o)} ${LABEL[o]}`).join(' · ') || 'No tritonIQ logins found.'}</p>
          {mode === 'preview' && count('create') > 0 && (
            <ul className="crm-import-notes">
              {rows.filter((r) => r.outcome === 'create' || r.outcome === 'error').slice(0, 200).map((r, i) => (
                <li key={i}>{r.company}: {r.outcome === 'error' ? `couldn't read (${r.message})` : `${r.name ?? ''} <${r.email}>`}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
